// Brand landing pages summarise Identifrag's own records. Every sentence here is assembled from
// stored fields (accord estimates, longevity category, published comparison counts), so a card or
// intro can never state something the catalogue does not already hold.

export type BrandComparisonCounts = {
  /** Published alternatives listed against this fragrance as the original. */
  alternatives: number;
  /** Originals this fragrance is published as an alternative to. */
  originals: number;
  /** Fragrances in their own right that this one is compared with. */
  related: number;
};
export type BrandAccord = { name: string; intensity: number };
export type BrandFragranceCard = {
  id: string;
  slug: string;
  name: string;
  concentration: string;
  description: string;
  isOriginal: boolean;
  gender?: '' | 'male' | 'female' | 'unisex';
  brand: { id: string; name: string; slug: string } | null;
  media: { id: string; alt: string; width: number; height: number } | null;
  comparisons: BrandComparisonCounts;
  /** Strongest recorded accords, most prominent first. */
  accords: BrandAccord[];
  longevity: string | null;
  /** Has published comparisons and a recorded concentration. */
  complete: boolean;
  reviews?: ReviewCounts;
};
export type ReviewCounts = { positive: number; negative: number };
export type BrandLandingStats = {
  fragrances: number;
  withComparisons: number;
  /** Fragrances listed as originals (including fragrances in their own right). */
  originals: number;
  complete: number;
  relationships: number;
  /** Recorded concentrations, most common first. */
  concentrations: string[];
  /** Most compared fragrances across the whole brand, for intro and meta copy. */
  top: { name: string; concentration: string; comparisons: number }[];
};
export type RelatedBrand = { id: string; name: string; slug: string; fragrances: number };
export type BrandLanding = {
  brand: { id: string; name: string; slug: string };
  stats: BrandLandingStats;
  mostCompared: BrandFragranceCard[];
  relatedBrands: RelatedBrand[];
  page: { items: BrandFragranceCard[]; total: number; page: number; pageSize: number };
};

// Hour bands for the recorded longevity categories. The fragrance comparison tables use the same
// bands, so a card and a detail page never describe one category differently.
export const longevityBands: Record<string, string> = {
  Light: 'Up to 4 hrs',
  Moderate: '4–6 hrs',
  'Long-lasting': '6–9 hrs',
  'Very long-lasting': '9+ hrs',
};

const concentrationAbbreviations: Record<string, string> = {
  'eau de toilette': 'EDT',
  'eau de parfum': 'EDP',
  'eau de cologne': 'EDC',
  'extrait de parfum': 'Extrait',
};
export function concentrationLabel(concentration: string) {
  const value = concentration.trim();
  return concentrationAbbreviations[value.toLocaleLowerCase('en')] ?? value;
}

/** Valid, non-zero accord estimates ordered by prominence; equal scores keep their recorded order. */
export function mainAccords(profile: unknown, limit = 5): BrandAccord[] {
  if (!profile || typeof profile !== 'object' || !('accords' in profile) || !Array.isArray(profile.accords)) return [];
  const seen = new Set<string>();
  const accords: BrandAccord[] = [];
  for (const accord of profile.accords as unknown[]) {
    if (!accord || typeof accord !== 'object') continue;
    const { name, intensity } = accord as { name?: unknown; intensity?: unknown };
    if (typeof name !== 'string' || !name.trim() || typeof intensity !== 'number') continue;
    if (!Number.isInteger(intensity) || intensity < 1 || intensity > 5) continue;
    const key = name.trim().toLocaleLowerCase('en');
    if (seen.has(key)) continue;
    seen.add(key);
    accords.push({ name: name.trim(), intensity });
  }
  return accords.sort((a, b) => b.intensity - a.intensity).slice(0, Math.max(0, limit));
}

export function recordedLongevity(profile: unknown): string | null {
  if (!profile || typeof profile !== 'object' || !('longevity' in profile)) return null;
  const value = profile.longevity;
  return typeof value === 'string' && value in longevityBands ? value : null;
}

export const comparisonTotal = (counts: BrandComparisonCounts) => counts.alternatives + counts.originals + counts.related;

const plural = (count: number, singular: string, pluralForm = singular + 's') => count + ' ' + (count === 1 ? singular : pluralForm);
function joinList(items: string[]) {
  return items.length <= 1 ? items.join('') : items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1];
}

/** Short count label for all comparison directions. */
export function comparisonCountLabel(counts: BrandComparisonCounts) {
  const total = comparisonTotal(counts);
  return total ? plural(total, 'comparison') : '';
}

export function cardActionLabel(card: Pick<BrandFragranceCard, 'comparisons'>) {
  return 'View fragrance';
}

/**
 * "Fresh, aquatic and sweet with woody accents. Currently linked to 4 comparisons."
 * Accords scored 3–5 lead the sentence; 2s (subtle) become accents; traces are left out.
 */
export function fragranceCardDescription(card: Pick<BrandFragranceCard, 'accords' | 'comparisons'>) {
  const sentences: string[] = [];
  const lead = card.accords.filter(accord => accord.intensity >= 3).slice(0, 3);
  if (lead.length) {
    const accents = card.accords.filter(accord => accord.intensity >= 2 && !lead.includes(accord)).slice(0, 2);
    const names = (list: BrandAccord[]) => joinList(list.map(accord => accord.name.toLocaleLowerCase('en')));
    const text = names(lead) + (accents.length ? ' with ' + names(accents) + ' accents' : '');
    sentences.push(text.charAt(0).toLocaleUpperCase('en') + text.slice(1) + '.');
  }
  return sentences.join(' ');
}

const byName = (a: BrandFragranceCard, b: BrandFragranceCard) =>
  a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }) || a.concentration.localeCompare(b.concentration) || a.id.localeCompare(b.id);

/** A neutral alphabetical directory; comparison counts never affect public ordering. */
export function sortBrandDirectory(cards: BrandFragranceCard[]) {
  return [...cards].sort(byName);
}

export function rankMostCompared(cards: BrandFragranceCard[], limit = 6, minimum = 1) {
  return cards
    .filter(card => comparisonTotal(card.comparisons) >= Math.max(1, minimum))
    .sort((a, b) => comparisonTotal(b.comparisons) - comparisonTotal(a.comparisons) || Number(b.complete) - Number(a.complete) || byName(a, b))
    .slice(0, limit);
}

/** Concentrations worth calling out: only when the brand has more than one recorded version. */
export function concentrationSummary(stats: Pick<BrandLandingStats, 'concentrations'>) {
  return [...new Set(stats.concentrations.map(concentrationLabel))];
}

export function brandIntro(brandName: string, stats: BrandLandingStats) {
  const sentences: string[] = [];
  const count = plural(stats.fragrances, brandName + ' fragrance');
  if (!stats.withComparisons) sentences.push(`Browse ${count} in the Identifrag directory. Comparisons for this house are still being reviewed.`);
  else {
    const all = stats.withComparisons === stats.fragrances;
    sentences.push(`Browse ${count} in the Identifrag directory and explore the comparisons linked to ${all ? 'each one' : 'them'}.`);
  }
  const concentrations = concentrationSummary(stats);
  if (concentrations.length > 1)
    sentences.push(`Concentrations are treated separately: ${joinList(concentrations)} versions each have their own comparisons, so you can match the one you wear.`);
  // Name a leader only when one fragrance clearly has more comparisons than the rest.
  const [top, next] = stats.top;
  if (top && top.comparisons > 1 && (!next || next.comparisons < top.comparisons))
    sentences.push(`The most compared is ${top.name}${top.concentration ? ' ' + concentrationLabel(top.concentration) : ''}, with ${plural(top.comparisons, 'published comparison')}.`);
  return sentences.join(' ');
}

export type BrandMetaSummary = Pick<BrandLandingStats, 'fragrances' | 'relationships' | 'concentrations' | 'top'>;

export function brandMetaDescription(brandName: string, summary: BrandMetaSummary) {
  return `Browse ${plural(summary.fragrances, brandName + ' fragrance')} on Identifrag, with scent profiles and concentrations where recorded.`;
}
